import React, { useEffect, useState } from 'react';

import {
  RiAlertLine,
  RiExternalLinkLine,
  RiFileCopyLine,
} from '@remixicon/react';

import { Button } from '@/design-system/button';
import { CheckboxField } from '@/design-system/checkbox';
import { FormModal } from '@/design-system/form-modal';
import { toast } from '@/design-system/toaster';

import { UJP_COPY } from '../copy';

/**
 * 6A — resend a Spend Control request whose last send ended UNKNOWN (the
 * connection dropped mid-send, so Basecamp may already hold it). The approver
 * must check Basecamp first: a link to the Spend Control list, a copy button
 * for the UJP reference to search with, and a required "Saya sudah cek"
 * before "Kirim ulang" unlocks. The parent owns the POST and sends it with
 * `force: true`; while it runs the modal cannot be dismissed.
 */

export interface ResendSpendRequestModalProps {
  open: boolean;
  /** The UJP reference — the subtitle, and what "Salin referensi" copies. */
  referenceId: string;
  /** Set when the request belongs to a biaya tambahan (subtitle "· Biaya tambahan #n"). */
  amendmentSeq?: number | null;
  /** Basecamp's Spend Control list page; the search link hides when null. */
  listUrl: string | null;
  sending: boolean;
  onClose: () => void;
  /** Send again with `force: true`. */
  onConfirm: () => void;
}

const ResendSpendRequestModal: React.FC<ResendSpendRequestModalProps> = ({
  open,
  referenceId,
  amendmentSeq,
  listUrl,
  sending,
  onClose,
  onConfirm,
}) => {
  const c = UJP_COPY.spend;
  const [checked, setChecked] = useState(false);

  // Every opening starts unchecked — the check is per resend, never remembered.
  useEffect(() => {
    if (!open) setChecked(false);
  }, [open]);

  const copyReference = () => {
    try {
      navigator.clipboard?.writeText(referenceId);
    } catch {
      /* clipboard unavailable — the reference is still in the subtitle */
    }
    toast.success(c.refCopied);
  };

  return (
    <FormModal
      open={open}
      onClose={sending ? () => undefined : onClose}
      icon={<RiAlertLine />}
      iconTone="warning"
      title={c.resendTitle}
      subtitle={
        amendmentSeq != null
          ? c.resendSubtitleAmendment(referenceId, amendmentSeq)
          : referenceId
      }
      size="sm"
      closeOnBackdropClick={!sending}
      closeOnEscape={!sending}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            onClick={onClose}
            disabled={sending}
          >
            {c.cancel}
          </Button>
          <Button
            onClick={onConfirm}
            loading={sending}
            disabled={!checked || sending}
            data-testid="ujp-spend-resend-confirm"
          >
            {sending ? c.busy : c.resend}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm leading-relaxed text-text-sub-600">{c.resendBody}</p>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {listUrl && (
            <a
              href={listUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[13px] font-semibold text-accent underline-offset-2 hover:underline"
            >
              {c.search}
              <RiExternalLinkLine className="size-3.5" aria-hidden />
            </a>
          )}
          <Button
            tone="neutral"
            style="ghost"
            size="xs"
            onClick={copyReference}
          >
            <RiFileCopyLine />
            {c.copyRef}
          </Button>
        </div>

        <CheckboxField
          id="ujp-spend-resend-checked"
          label={c.checked}
          checked={checked}
          onCheckedChange={(v) => setChecked(v === true)}
          disabled={sending}
        />
      </div>
    </FormModal>
  );
};

export default ResendSpendRequestModal;
